import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import FAQS from "./faqs";
import "./howitworks.css";

const CONSUMER_SITE = "https://qiktruck.co.za";
const QUOTE_URL = `${CONSUMER_SITE}/quote`;
const WHATSAPP = "https://wa.me/+27663784460";
const SUPPORT_EMAIL = "support@qiktruck.co.za";

const STEPS = [
  {
    icon: "mdi-calculator-variant",
    title: "Get an instant quote",
    copy: (
      <>
        Enter pickup and drop-off, pick your vehicle size, and see a transparent
        all-in price in seconds.{" "}
        <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer">
          Try it free
        </a>
        .
      </>
    ),
  },
  {
    icon: "mdi-clipboard-check-outline",
    title: "Confirm your booking",
    copy:
      "Review the price, add any notes about your load, and submit your order. Pay now or choose pay-later where available.",
  },
  {
    icon: "mdi-truck-delivery",
    title: "Driver arrives on site",
    copy:
      "A vetted driver, helper, and truck come to you. Track progress live and communicate through the platform.",
  },
  {
    icon: "mdi-credit-card-outline",
    title: "Pay your way",
    copy:
      "Settle online, by EFT, or with cash when the job starts or after delivery - whatever works for you.",
  },
];

const FEATURES = [
  {
    icon: "mdi-map-marker-radius",
    title: "Live tracking",
    text: "Follow your driver from pickup to drop-off in real time.",
  },
  {
    icon: "mdi-shield-check",
    title: "Vetted fleet",
    text: "Vehicles inspected and drivers background-checked before they go live.",
  },
  {
    icon: "mdi-cash-multiple",
    title: "Transparent pricing",
    text: "Driver and fuel included upfront - no surprise add-ons at the gate.",
  },
  {
    icon: "mdi-earth",
    title: "Nationwide",
    text: "Bakkies to heavy trucks, available across all SA provinces.",
  },
];

const PAYMENTS = [
  { icon: "mdi-credit-card", label: "Card online" },
  { icon: "mdi-bank", label: "EFT / bank transfer" },
  { icon: "mdi-cash", label: "Cash on site" },
];

const NAV_LINKS = [
  { id: "about", label: "About" },
  { id: "howitworks", label: "How it works" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
];

const PAGE_META = {
  "/howitworks": {
    title: "How QikTruck works",
    lead:
      "From instant quote to goods delivered - four simple steps to move furniture, equipment, rubble, and more across South Africa.",
  },
  "/contact": {
    title: "Contact QikTruck",
    lead:
      "Questions about a booking, business account, or partnership? Our team is here to help.",
  },
  "/about": {
    title: "About QikTruck",
    lead:
      "South Africa's on-demand truck and bakkie hire platform - connecting you with local, vetted drivers when you need to move heavy goods.",
  },
};

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function Section({ location }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeNav, setActiveNav] = useState("howitworks");

  const path = location.pathname.replace(/\/$/, "") || "/about";
  const meta = PAGE_META[path] || PAGE_META["/about"];

  useEffect(() => {
    document.title = `${meta.title} · QikTruck`;

    const hash = (location.hash || "").replace("#", "");
    if (hash) {
      requestAnimationFrame(() => scrollToSection(hash));
      setActiveNav(hash);
    } else if (path === "/contact") {
      requestAnimationFrame(() => scrollToSection("contact"));
      setActiveNav("contact");
    } else if (path === "/howitworks") {
      setActiveNav("howitworks");
    } else if (path === "/about") {
      setActiveNav("about");
    }
  }, [location.hash, path, meta.title]);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => l.id);
    const onScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY < 200) {
        const routeDefault =
          path === "/contact"
            ? "contact"
            : path === "/howitworks"
              ? "howitworks"
              : "about";
        setActiveNav(routeDefault);
        return;
      }
      let current = sections[0];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          current = id;
        }
      }
      setActiveNav(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [path]);

  const toggleFaq = (idx) => {
    setOpenFaq((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="qt-hiw">
      <header className="qt-hiw__hero">
        <div className="qt-hiw__hero-inner">
          <span className="qt-hiw__eyebrow">Move anything, anytime</span>
          <h1 className="qt-hiw__title">{meta.title}</h1>
          <p className="qt-hiw__lead">{meta.lead}</p>
          <div className="qt-hiw__hero-actions">
            <a
              className="qt-hiw__btn-primary"
              href={QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get instant quote
              <i className="mdi mdi-arrow-right" />
            </a>
            <a className="qt-hiw__btn-ghost" href="/signup">
              Business sign up
              <i className="mdi mdi-office-building" />
            </a>
          </div>
        </div>
      </header>

      <nav className="qt-hiw__nav" aria-label="Page sections">
        <div className="qt-hiw__nav-inner">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={activeNav === link.id ? "is-active" : ""}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.id);
                setActiveNav(link.id);
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      </nav>

      <section className="qt-hiw__section" id="about">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">About us</span>
          <h2 className="qt-hiw__section-title">South Africa&apos;s local truck hire network</h2>
          <div className="qt-hiw__about-grid">
            <div className="qt-hiw__about-copy">
              <p>
                QikTruck connects people who need to move heavy goods - furniture,
                equipment, construction materials, rubble, and more - with affordable
                local trucks and vetted drivers in their community.
              </p>
              <p>
                Whether you&apos;re clearing a home, restocking a store, or moving
                machinery between sites, you get an instant price, a driver assigned
                fast, and full visibility from pickup to delivery.
              </p>
            </div>
            <div className="qt-hiw__about-visual">
              <img
                src="https://media.qiktruck.co.za/3d-1.5tonopen.png"
                alt="QikTruck delivery vehicle"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="qt-hiw__section qt-hiw__section--alt" id="howitworks">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">The process</span>
          <h2 className="qt-hiw__section-title">Four steps from quote to delivery</h2>
          <p className="qt-hiw__section-sub">
            No phone calls, no waiting for callbacks. Book a truck with driver and
            helper in minutes.
          </p>

          <div className="qt-hiw__steps">
            {STEPS.map((step, i) => (
              <article className="qt-hiw__step" key={step.title}>
                <div className="qt-hiw__step-num">{i + 1}</div>
                <div className="qt-hiw__step-icon">
                  <i className={`mdi ${step.icon}`} />
                </div>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="qt-hiw__section">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">Why QikTruck</span>
          <h2 className="qt-hiw__section-title">Built for peace of mind</h2>
          <p className="qt-hiw__section-sub">
            Every booking is backed by platform standards - not just a random truck
            off a classifieds site.
          </p>
          <div className="qt-hiw__features">
            {FEATURES.map((f) => (
              <div className="qt-hiw__feature" key={f.title}>
                <i className={`mdi ${f.icon}`} />
                <div>
                  <strong>{f.title}</strong>
                  <span>{f.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="qt-hiw__section qt-hiw__section--alt">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">Payments</span>
          <h2 className="qt-hiw__section-title">Flexible ways to pay</h2>
          <p className="qt-hiw__section-sub">
            Pay when it suits you - at booking, when the driver arrives, or after
            the job is done.
          </p>
          <div className="qt-hiw__payments">
            {PAYMENTS.map((p) => (
              <span className="qt-hiw__payment-chip" key={p.label}>
                <i className={`mdi ${p.icon}`} />
                {p.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="qt-hiw__section" id="faq">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">FAQ</span>
          <h2 className="qt-hiw__section-title">Common questions</h2>
          <p className="qt-hiw__section-sub">
            Quick answers before you book. Need more detail?{" "}
            <a href="#contact">Get in touch</a>.
          </p>
          <div className="qt-hiw__faq">
            {FAQS.map((item, idx) => (
              <div
                className={`qt-hiw__faq-item${openFaq === idx ? " is-open" : ""}`}
                key={item.q}
              >
                <button
                  type="button"
                  className="qt-hiw__faq-trigger"
                  onClick={() => toggleFaq(idx)}
                  aria-expanded={openFaq === idx}
                >
                  {item.q}
                  <i className="mdi mdi-chevron-down" />
                </button>
                <div className="qt-hiw__faq-body">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="qt-hiw__section qt-hiw__section--alt" id="contact">
        <div className="qt-hiw__inner">
          <span className="qt-hiw__section-label">Get in touch</span>
          <h2 className="qt-hiw__section-title">We&apos;re here to help</h2>
          <p className="qt-hiw__section-sub">
            Booking questions, business accounts, driver partnerships, or
            insurance - reach out anytime.
          </p>
          <div className="qt-hiw__contact-grid">
            <a className="qt-hiw__contact-card" href={WHATSAPP} target="_blank" rel="noopener noreferrer">
              <i className="mdi mdi-whatsapp" />
              <strong>WhatsApp</strong>
              <span>+27 66 378 4460 - fastest for urgent queries</span>
            </a>
            <a className="qt-hiw__contact-card" href={`mailto:${SUPPORT_EMAIL}`}>
              <i className="mdi mdi-email-outline" />
              <strong>Email</strong>
              <span>{SUPPORT_EMAIL}</span>
            </a>
            <a className="qt-hiw__contact-card" href="/support">
              <i className="mdi mdi-lifebuoy" />
              <strong>Support centre</strong>
              <span>Help articles and ticket support for account holders</span>
            </a>
          </div>
        </div>
      </section>

      <section className="qt-hiw__cta">
        <div className="qt-hiw__cta-inner">
          <h2>Ready to move something?</h2>
          <p>
            Get your price in under 30 seconds - no account required for a quote.
          </p>
          <a
            className="qt-hiw__btn-primary"
            href={QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get instant quote
            <i className="mdi mdi-truck-fast" />
          </a>
        </div>
      </section>
    </div>
  );
}

export default withRouter(Section);
